// pages/Menu/dashboard.jsx
import React, { useEffect, useState } from "react";
import { Routes, Route, Link, Navigate, useNavigate } from "react-router-dom";
import { Wallet, Building2, Link2, ChevronRight, AlertTriangle } from "lucide-react";
import { Banner } from "../../components/common/Banner";

/* Servicios (para contar pendientes) */
import { listMembershipApplications } from "../../services/membershipApplications";

/* Articulación (rutas siguen disponibles) */
import Directory from "./Articulacion/Directory";
import RegionalEntities from "./RegionalEntities";
import Minutes from "./Articulacion/Documents/Minutes";
import ProjectsPage from "./Articulacion/Documents/ProjectsPage";
import WorktablesHome from "./Articulacion/Documents/WorktablesHome";

/* Vinculación */
import MembershipApplicationsSection from "./Vinculation/panel/MembershipApplicationsSection";
import CRMBoard from "./Vinculation/CRM/CRMBoard";
import CRMIndicators from "./Vinculation/CRM/CRMIndicators";
import OtherCRMBoard from "./Vinculation/CRM External/OtherCRMBoard";

/* Finanzas */
import IncomeList from "./Finance/IncomeList";
import OutcomeList from "./Finance/OutcomeList";
import FinancialReports from "./Finance/FinancialReports";

/* -------------------- Badge de precaución (pendientes) -------------------- */
const PrecautionBadge = ({ count }) => {
  if (!count || count <= 0) return null;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50
                 text-amber-800 px-2.5 h-6 text-xs font-semibold shadow-sm ml-2"
      title={`${count} postulación(es) pendiente(s)`}
    >
      <AlertTriangle className="w-3.5 h-3.5 animate-pulse" />
      {count}
    </span>
  );
};

/* -------------------- Tarjeta simple (botón) -------------------- */
const CardLink = ({ icon: Icon, title, desc, to }) => {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(to)}
      className="w-full h-full text-left bg-white rounded-2xl border border-gray-200 shadow-sm
                 hover:shadow-md transition-all p-6 focus:outline-none flex flex-col"
    >
      <div className="flex items-start gap-4 flex-1">
        <div className="p-3 rounded-xl bg-blue-50 border border-blue-100">
          <Icon className="w-6 h-6 text-[#0488AF]" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className="text-sm text-gray-600 mt-1">{desc}</p>
        </div>
      </div>
    </button>
  );
};

/* -------------------- Tarjeta con Submenú Hover -------------------- */
function CardWithSubmenu({ icon: Icon, title, desc, children, badge }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative group h-full">
      <div
        className="w-full h-full text-left bg-white rounded-2xl border border-gray-200 shadow-sm
                   hover:shadow-md transition-all p-6 focus:outline-none flex flex-col"
      >
        <div className="flex items-start gap-4 flex-1">
          <div className="p-3 rounded-xl bg-blue-50 border border-blue-100">
            <Icon className="w-6 h-6 text-[#0488AF]" />
          </div>
          <button
            type="button"
            className="text-left flex-1"
            onClick={() => setOpen(!open)}
            aria-haspopup="true"
            aria-expanded={open}
          >
            <div className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <span>{title}</span>
              {badge /* ⬅️ Badge en el título del padre */}
              <ChevronRight
                className={`w-4 h-4 transition-transform duration-200
                           ${open ? "rotate-90" : "group-hover:rotate-90"}`}
              />
            </div>
            <p className="text-sm text-gray-600 mt-1">{desc}</p>
          </button>
        </div>
      </div>

      <div
        className={`
          absolute left-0 top-[calc(100%+8px)] z-20 min-w-[260px]
          rounded-2xl border border-gray-200 bg-white shadow-xl p-2
          opacity-0 invisible translate-y-1
          group-hover:opacity-100 group-hover:visible group-hover:translate-y-0
          transition-all duration-200
          ${open ? "!opacity-100 !visible !translate-y-0" : ""}
        `}
        role="menu"
      >
        {children}
      </div>
    </div>
  );
}

function SubItem({ to, title, desc }) {
  return (
    <Link
      to={to}
      className="flex gap-3 items-start rounded-xl p-3 hover:bg-blue-50/60 focus:bg-blue-50/60 outline-none"
      role="menuitem"
    >
      <div className="mt-1">
        <div className="font-semibold text-[#0b3a66]">{title}</div>
        <div className="text-sm text-gray-600">{desc}</div>
      </div>
    </Link>
  );
}

/* -------------------- HOME del Panel -------------------- */
const PanelHome = () => {
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // Buscamos "Pendientes" (status RECEIVED). Intentamos leer total desde meta.total.
        const res = await listMembershipApplications({ status: "RECEIVED", page: 1, pageSize: 1 });
        const items = Array.isArray(res) ? res : (res?.data ?? []);
        const total = (res?.meta?.total ?? res?.total ?? (items?.length ?? 0));
        if (alive) setPendingCount(Number(total) || (items?.length ?? 0));
      } catch {
        if (alive) setPendingCount(0);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div className="flex flex-col min-h-screen w-full max-w-full px-4 md:px-8 py-4 md:py-6">
      <div className="max-w-[1100px] mx-auto w-full space-y-6">
        {/* Banner arriba */}
        <div className="relative">
          <Banner
            title="Área de trabajo"
            message="Accesos rápidos a Finanzas, Vinculación Empresarial y Articulación Público-Privada."
          />
        </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Finanzas -> con submenú */}
          <CardWithSubmenu
            icon={Wallet}
            title="Finanzas"
            desc="Cargar/editar egresos e ingresos, ver resúmenes y exportar reportes."
          >
            <SubItem
              to="/panel/finanzas/ingresos"
              title="Ingresos"
              desc="Registrar y gestionar ingresos por concepto en el libro diario."
            />
            <SubItem
              to="/panel/finanzas/egresos"
              title="Egresos"
              desc="Registrar y gestionar egresos por concepto en el libro diario."
            />
            <SubItem
              to="/panel/finanzas/reportes"
              title="Reportes Financieros"
              desc="Estado de resultados, flujos de caja y balances comparativos."
            />
          </CardWithSubmenu>

          {/* Vinculación Empresarial - con badge en el título del padre */}
          <CardWithSubmenu
            icon={Link2}
            title="Vinculación Empresarial"
            desc="Postulaciones y Relacionamiento."
            badge={<PrecautionBadge count={pendingCount} />}
          >
            <SubItem
              to="/panel/vinculacion-empresarial/postulaciones"
              title="Panel de postulación de socios"
              desc="Revisar y gestionar solicitudes."
            />
            <SubItem
              to="/panel/vinculacion-empresarial/crm"
              title="Relacionamiento con socios"
              desc="Tarjetas por empresa, reuniones, minutas e indicadores"
            />
            <SubItem
              to="/panel/vinculacion-empresarial/crm-external"
              title="Relacionamiento con otras entidades"
              desc="Tarjetas por entidad, reuniones, minutas e indicadores"
            />
          </CardWithSubmenu>

          {/* Articulación Público-Privada */}
          <CardWithSubmenu
            icon={Building2}
            title="Articulación Público-Privada"
            desc="Directorio digital y mesas de trabajo."
          >
            <SubItem
              to="/panel/articulacion/directorio"
              title="Directorios de contactos"
              desc="Contactos, filtros, búsqueda y acciones rápidas."
            />
            <SubItem
              to="/panel/articulacion/minutas"
              title="Banco de minutas"
              desc="Subir, etiquetar y buscar minutas con trazabilidad."
            />
            <SubItem
              to="/panel/articulacion/proyectos"
              title="Proyectos externos"
              desc="Proyectos asociados a entidades del directorio."
            />
            <SubItem
              to="/panel/articulacion/mesas"
              title="Mesas de trabajo"
              desc="Crear mesas, subir minutas y ordenar por fecha/entidad."
            />
          </CardWithSubmenu>
        </div>
      </div>
    </div>
  );
};

/* -------------------- Router interno de /panel -------------------- */
export default function XDashboard() {
  return (
    <Routes>
      {/* /panel */}
      <Route index element={<PanelHome />} />

      {/* Finanzas */}
      <Route path="finanzas" element={<Navigate to="finanzas/ingresos" replace />} />
      <Route path="finanzas/ingresos" element={<IncomeList />} />
      <Route path="finanzas/egresos" element={<OutcomeList />} />
      <Route path="finanzas/reportes" element={<FinancialReports />} />

      {/* Vinculación Empresarial */}
      <Route
        path="vinculacion-empresarial"
        element={<Navigate to="vinculacion-empresarial/postulaciones" replace />}
      />
      <Route
        path="vinculacion-empresarial/postulaciones"
        element={<MembershipApplicationsSection onClose={() => window.history.back()} />}
      />
      <Route path="vinculacion-empresarial/crm" element={<CRMBoard />} />
      <Route path="vinculacion-empresarial/Indicators" element={<CRMIndicators />} />
      <Route path="vinculacion-empresarial/crm-external" element={<OtherCRMBoard />} />

      {/* Articulación (todas sus rutas activas) */}
      <Route path="articulacion/directorio" element={<Directory />} />
      <Route path="articulacion/entidades" element={<RegionalEntities />} />
      <Route path="articulacion/minutas" element={<Minutes />} />
      <Route path="articulacion/proyectos" element={<ProjectsPage />} />
      <Route path="articulacion/mesas" element={<WorktablesHome />} />

      {/* Fallback interno del panel */}
      <Route path="*" element={<Navigate to="." replace />} />
    </Routes>
  );
}
